import { useCallback } from 'react';
import cn from 'classnames';
import { orderBy } from 'lodash/fp';

import { beClientProfileSalonTags } from 'actions/client/beSidebarClientProfile.action';
import Button from 'components/form/Button.component';
import LINK from 'constants/link.const';
import { useNavFor } from 'hooks/useNav.hook';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToLowerCase from 'utils/converters/any/fromAnyToLowerCase.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import linkTo from 'utils/links/linkTo.util';

import CN from './ClientProfileTags.module.scss';

const DISPLAYED_TAGS_COUNT = 5;
const MAX_SELECTED_TAGS = 3;

export const ClientProfileTags = ({ salonId, selectedTags: selectedTagsFromProps, onTagChange = alwaysNoop }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { val: isAllTags, tip: toggleAllTags } = useTip(false);

  // Fetch salon tags using React Query
  const { qj: salonTagsData } = usePrepareReadOnly(beClientProfileSalonTags);
  const tags = asArray(salonTagsData);
  const selectedTags = asArray(selectedTagsFromProps);

  const nav = useNavFor('ClientProfileTags');

  const onTagClick = useCallback(
    (tagId) => {
      const isCurrentlySelected = selectedTags.some((t) => t.id === tagId);

      if (isCurrentlySelected) {
        // Deselect the tag
        const filteredTags = selectedTags.filter((t) => t.id !== tagId);
        call('ClientProfileTags.onTagChange.deselect', onTagChange, { value: filteredTags });
      } else {
        // Select the tag if we haven't reached the limit
        if (selectedTags.length < MAX_SELECTED_TAGS) {
          const tagToAdd = tags.find((t) => t.id === tagId);

          if (tagToAdd) {
            call('ClientProfileTags.onTagChange.select', onTagChange, { value: [...selectedTags, tagToAdd] });
          }
        }
      }
    },
    [onTagChange, selectedTags, tags],
  );

  const navigateToTagsPage = useCallback(() => {
    nav.push(
      linkTo({
        pattern: LINK.slnTags,
        params: { salonId },
      }),
    );
  }, [nav, salonId]);

  // Show selected tags first, then the rest sorted by name
  const isTagSelected = (tag) => selectedTags.some((item) => item.id === tag.id);
  const sortedTags = orderBy([(tag) => !isTagSelected(tag), (tag) => fromAnyToLowerCase(tag.name || '')], ['asc', 'asc'], tags);

  // Display first 5 tags or all if expanded
  const displayedTags = isAllTags ? sortedTags : sortedTags.slice(0, DISPLAYED_TAGS_COUNT);

  const hasMoreTags = sortedTags.length > DISPLAYED_TAGS_COUNT;
  const isAtSelectionLimit = selectedTags.length >= MAX_SELECTED_TAGS;

  return (
    <div data-bem="ClientProfileTags" className={cn(CN.tagsContainer, { [CN.dark]: isDarkMode })}>
      <div className={CN.tagsList}>
        {displayedTags.map((tag) => {
          const isSelected = selectedTags.some((item) => item.id === tag.id);
          const isDisabled = !isSelected && isAtSelectionLimit;

          return (
            <Button key={tag.id} variant="tag" onClick={() => onTagClick(tag.id)} selected={isSelected} disabled={isDisabled} aria-label={`${isSelected ? 'Remove' : 'Add'} tag: ${tag.name}`}>
              <span className={CN.tagEmoji}>{tag.emoticon || ''}</span>
              <span className={CN.tagName}>{tag.name}</span>
            </Button>
          );
        })}

        {(isAllTags || (!isAllTags && !hasMoreTags)) && (
          <Button variant="tagEmphasis" onClick={navigateToTagsPage}>
            {t('composite.clientTags.tagsButton')}
          </Button>
        )}
      </div>

      {hasMoreTags && (
        <Button variant="secondary" onClick={toggleAllTags} noMargin noVerticalMargin noPadding height="auto">
          <span className={CN.viewMoreText}>{isAllTags ? t('composite.clientTags.lessTagsButton') : t('composite.clientTags.moreTagsButton')}</span>
        </Button>
      )}
    </div>
  );
};
